/**
 * Фотография сотрудника — ровно один способ её получить.
 *
 * Способов было два: `<Image source={{uri, headers}}>` на
 * `/control/personnel/{id}/avatar` в списке людей и `fetchPersonnel(record_id)` ради
 * одного поля `avatar_base64` в ленте доступа. Остался один —
 * {@link fetchPersonnelAvatar}, и он один на все списки кабинета.
 *
 * Исключение ровно одно и оно не про списки: экран, у которого карточка человека уже
 * ЕСТЬ (`PersonCard`, «Моя посещаемость»), берёт `avatar_base64` оттуда — просить
 * второй раз то, что уже в руках, незачем.
 *
 * ## Что здесь выиграно, а что нет
 *
 * Выиграно: лента доступа больше не тянет ПОЛНУЮ карточку человека (полсотни полей) на
 * каждую строку ради одной картинки, и все списки кабинета делят один ключ кэша — лицо,
 * увиденное в отчёте, ничего не стоит ведомости и ленте.
 *
 * ⚠️ **Не выиграно: старый путь через `<Image>` в браузере не был сломан.** Это было
 * записано здесь как факт 2026-08-27 и оказалось НЕВЕРНО. Рассуждение звучало так:
 * react-native-web рисует `<Image>` как обычный `<img>`, `<img>` не несёт
 * пользовательских заголовков (проверено перехватом — уходят только `accept`,
 * `accept-language`, `referer`, `user-agent`, `cookie`), значит `X-Company-Id` теряется
 * и вызов попадает в домен `system`, куда владельцу компании нельзя.
 *
 * Ошибка в последнем шаге. Веб-сборка держит сессию в HttpOnly-куках (`sv_access`,
 * `sv_refresh`), и браузер прикладывает их к `<img>` сам, то есть запрос авторизован; а
 * `record_id`, существующий ровно в одной компании, гейтвей резолвит и без заголовка
 * компании. Проверено на боевом гейтвее: голый `new Image()` со страницы кабинета
 * грузит JPEG 960×1280.
 *
 * Оставлено это переписывание ради первого абзаца, а не ради второго. Если однажды
 * покажется, что оно лишнее, — так и есть, минус общий кэш и минус лишний обход по
 * ленте.
 */
import {ApiError, requestControlRaw} from '@/shared/api';

/**
 * Фото через API-клиент.
 *
 * `requestControlRaw` ставит `X-Company-Id` и `Authorization`, обновляет протухший
 * токен и повторяет запрос, и ведёт себя одинаково на вебе и на телефоне. Плата — байты
 * больше не попадают в кэш картинок платформы, поэтому результат живёт значением
 * react-query с длинным `staleTime` (см. `PersonAvatar`), а не перезапрашивается на
 * каждый рендер.
 *
 * Returns `null`, never throws, for the two answers that mean «no face to show»: `404`
 * (the record has no enrolled photo) and `403` (this reader may not look at personnel).
 * Both are ordinary states of a list of people, and neither should turn a row into an
 * error.
 */
export async function fetchPersonnelAvatar(
  companyId: string,
  recordId: string,
): Promise<string | null> {
  let res: Response;
  try {
    res = await requestControlRaw(
      `/control/personnel/${encodeURIComponent(recordId)}/avatar`,
      companyId,
    );
  } catch (error) {
    if (error instanceof ApiError && (error.status === 404 || error.status === 403)) {
      return null;
    }
    throw error;
  }
  return responseToDataUri(res);
}

/**
 * Байты картинки → `data:`-URI, который умеют показать и `<Image>` в RN, и `<img>` в
 * вебе.
 *
 * Кодирование base64 написано руками, хотя в каждой среде есть готовое. Причина в том,
 * что готовое в каждой среде РАЗНОЕ: `FileReader.readAsDataURL` есть в браузере и в
 * React Native и нет в Node, где идут тесты; `btoa` есть в браузере и не гарантирован
 * в RN; `Buffer` есть в Node и нет ни там, ни там. Пятнадцать строк без единого
 * глобального объекта — это одна ветка кода на три среды вместо трёх веток, из
 * которых проверить можно две.
 *
 * `arrayBuffer()` при этом есть везде и берётся у ответа напрямую.
 */
const B64 =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

/* eslint-disable no-bitwise -- base64 is bit-shuffling; that is the whole algorithm. */
function toBase64(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const a = bytes[i];
    const b = bytes[i + 1];
    const c = bytes[i + 2];
    out += B64[a >> 2];
    out += B64[((a & 3) << 4) | ((b ?? 0) >> 4)];
    // Хвост короче трёх байт добивается '=' — иначе декодер получит лишние нули.
    out += b === undefined ? '=' : B64[((b & 15) << 2) | ((c ?? 0) >> 6)];
    out += c === undefined ? '=' : B64[c & 63];
  }
  return out;
}
/* eslint-enable no-bitwise */

async function responseToDataUri(res: Response): Promise<string | null> {
  const bytes = new Uint8Array(await res.arrayBuffer());
  if (bytes.length === 0) {
    return null;
  }
  // Тип берётся у ответа, а не угадывается по первым байтам, как приходится делать с
  // `avatar_base64`: здесь его прислал сервер.
  const mime = res.headers.get('content-type') || 'image/jpeg';
  return `data:${mime};base64,${toBase64(bytes)}`;
}
